import { getDb } from '../db/db';
import { Waybill } from '../../types';
import localforage from 'localforage';
import { DB_KEYS } from '../dbKeys';
import { cloudSync } from '../sync/CloudSyncService';
import { encrypt, decrypt } from '../security/encryption';

/**
 * Safe UUID generation.
 * Works even in old browsers and insecure contexts (HTTP).
 */
function safeUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        try {
            return crypto.randomUUID();
        } catch (e) {
            // Fall through to fallback
        }
    }
    // Fallback: UUID v4 via Math.random
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// Флаг, чтобы не запускать миграцию при каждом запросе, если она уже прошла в этой сессии
let migrationChecked = false;

async function ensureMigration() {
    if (migrationChecked) return;

    const db = await getDb();
    const count = await db.count('waybills');

    // Если в новой таблице пусто, проверяем старое хранилище
    if (count === 0) {
        console.log('[WaybillRepo] Store is empty, checking for legacy data...');
        try {
            // Пробуем 2 варианта: blob и granular store

            // 1. Сначала проверяем granular store (как записывает импорт через createRepo)
            const granularStore = localforage.createInstance({
                name: 'AppDB',
                storeName: DB_KEYS.WAYBILLS
            });

            let legacyCount = 0;
            const legacyItems: Waybill[] = [];

            await granularStore.iterate((value: Waybill) => {
                legacyItems.push(value);
                legacyCount++;
            });

            if (legacyCount > 0) {
                console.log(`[WaybillRepo] Found ${legacyCount} items in granular store. Migrating...`);

                const tx = db.transaction('waybills', 'readwrite');
                const store = tx.objectStore('waybills');
                await Promise.all(legacyItems.map(item => store.put(item)));
                await tx.done;

                console.log('[WaybillRepo] Migration from granular store successful.');
                migrationChecked = true;
                return;
            }

            // 2. Fallback: проверяем blob (старый формат)
            const legacyBlob = await localforage.getItem<Waybill[]>(DB_KEYS.WAYBILLS);

            if (legacyBlob && Array.isArray(legacyBlob) && legacyBlob.length > 0) {
                console.log(`[WaybillRepo] Found ${legacyBlob.length} legacy blob items. Migrating...`);

                const tx = db.transaction('waybills', 'readwrite');
                const store = tx.objectStore('waybills');
                await Promise.all(legacyBlob.map(item => store.put(item)));
                await tx.done;

                console.log('[WaybillRepo] Migration from blob successful. Clearing legacy blob.');
                await localforage.removeItem(DB_KEYS.WAYBILLS);
            } else {
                console.log('[WaybillRepo] No legacy data found in either format.');
            }
        } catch (e) {
            console.error('[WaybillRepo] Migration failed', e);
        }
    }
    migrationChecked = true;
}

// Force re-run migration (useful after import)
export async function forceMigration() {
    console.log('[WaybillRepo] Forcing migration...');
    migrationChecked = false;
    await ensureMigration();
}

export const waybillRepository = {
    // --- Basic CRUD via idb direct access ---

    async getById(id: string): Promise<Waybill | undefined> {
        await ensureMigration();
        const db = await getDb();
        return db.get('waybills', id);
    },

    async save(waybill: Waybill): Promise<void> {
        const db = await getDb();
        // Используем put, чтобы перезаписать или создать.
        // Это автоматически обновит индексы.
        await db.put('waybills', waybill);

        // Cloud Sync with DATA MINIMIZATION
        await cloudSync.syncCreate('waybills', waybill.id, filterWaybillForCloud(waybill));
    },

    async delete(id: string, skipSync = false): Promise<void> {
        const db = await getDb();
        await db.delete('waybills', id);

        // Cloud Sync
        if (!skipSync) {
            await cloudSync.syncDelete('waybills', id);
        }
    },

    /**
     * Create a new waybill. Generates ID if not provided.
     * Returns the created waybill with ID.
     */
    async create(item: Omit<Waybill, 'id'> & Partial<Pick<Waybill, 'id'>>): Promise<Waybill> {
        try {
            console.log('[WaybillRepo] CREATE: Starting...', item.number);
            const db = await getDb();
            console.log('[WaybillRepo] CREATE: Got DB connection');
            const id = item.id ?? safeUUID();
            const waybill = { ...item, id } as Waybill;
            console.log('[WaybillRepo] CREATE: Putting to store, id=', id);
            await db.put('waybills', waybill);
            console.log('[WaybillRepo] CREATE: SUCCESS, id=', id);

            // Cloud Sync with DATA MINIMIZATION
            await cloudSync.syncCreate('waybills', id, filterWaybillForCloud(waybill));

            return waybill;
        } catch (e) {
            console.error('[WaybillRepo] CREATE: FAILED', e);
            throw e;
        }
    },

    /**
     * Update an existing waybill.
     * Merges patch with existing data.
     */
    async update(id: string, patch: Partial<Waybill>): Promise<Waybill> {
        try {
            console.log('[WaybillRepo] UPDATE: Starting...', id);
            await ensureMigration();
            const db = await getDb();
            const existing = await db.get('waybills', id);
            if (!existing) {
                throw new Error(`Waybill not found: ${id}`);
            }
            const updated = { ...existing, ...patch } as Waybill;
            console.log('[WaybillRepo] UPDATE: Putting to store, id=', id);
            await db.put('waybills', updated);
            console.log('[WaybillRepo] UPDATE: SUCCESS, id=', id);

            // Cloud Sync with DATA MINIMIZATION (syncUpdate with whole object to ensure filter)
            await cloudSync.syncCreate('waybills', id, filterWaybillForCloud(updated));

            return updated;
        } catch (e) {
            console.error('[WaybillRepo] UPDATE: FAILED', e);
            throw e;
        }
    },

    /**
     * Get all waybills (for compatibility with repo.ts pattern).
     */
    async getAll(): Promise<Waybill[]> {
        await ensureMigration();
        const db = await getDb();
        return db.getAll('waybills');
    },

    /**
     * Bulk update waybills in a single transaction.
     */
    async updateBulk(items: Waybill[]): Promise<void> {
        if (items.length === 0) return;
        const db = await getDb();
        const tx = db.transaction('waybills', 'readwrite');
        const store = tx.objectStore('waybills');
        await Promise.all(items.map(item => store.put(item)));
        await tx.done;

        // Cloud Sync with DATA MINIMIZATION
        const payloadPromises = items.map(item => filterWaybillForCloud(item));
        const payloads = await Promise.all(payloadPromises);
        await Promise.all(payloads.map((payload, i) => cloudSync.syncCreate('waybills', items[i].id, payload)));
    },

    async deleteBulk(ids: string[], skipSync = false): Promise<void> {
        if (ids.length === 0) return;
        const db = await getDb();
        const tx = db.transaction('waybills', 'readwrite');
        const store = tx.objectStore('waybills');
        await Promise.all(ids.map(id => store.delete(id)));
        await tx.done;

        // Cloud Sync
        if (!skipSync) {
            await Promise.all(ids.map(id => cloudSync.syncDelete('waybills', id)));
        }
    },

    /**
     * Restore/Sync waybills from cloud.
     * Decrypts number if needed.
     */
    async upsertFromCloud(items: any[]): Promise<void> {
        if (items.length === 0) return;
        const security = await cloudSync.getEncryptionParams();

        // 1. Prepare items (Async decryptions yield event loop, killing IDB transaction if opened early)
        const processedItems = await Promise.all(items.map(async (item) => {
            // 1. Decrypt number
            if (security && item.number && typeof item.number === 'string' && item.number.length > 20) {
                try {
                    const decrypted = await decrypt(item.number, security.key || security.password, security.salt);
                    if (decrypted) item.number = decrypted;
                } catch (e) {
                    // Ignore, keep original
                }
            }

            // 2. Decrypt Routes (JSON string)
            if (security && item.routes && typeof item.routes === 'string') {
                try {
                    const decryptedRoutesJson = await decrypt(item.routes, security.key || security.password, security.salt);
                    if (decryptedRoutesJson) {
                        item.routes = JSON.parse(decryptedRoutesJson);
                    }
                } catch (e) {
                    console.warn('[WaybillRepo] Failed to decrypt routes', e);
                    item.routes = []; // Fallback to empty if decryption fails
                }
            }

            // 3. Decrypt Notes
            if (security && item.notes && typeof item.notes === 'string') {
                try {
                    const decryptedNotes = await decrypt(item.notes, security.key || security.password, security.salt);
                    if (decryptedNotes) item.notes = decryptedNotes;
                } catch (e) {
                    // Ignore
                }
            }

            // Ensure it matches Waybill type requirements (defaults)
            return {
                ...item,
                routes: Array.isArray(item.routes) ? item.routes : [], // Ensure routes is array
                status: item.status || 'draft',
                calculationMethod: item.calculationMethod || 'by_total',
                createdFrom: 'cloud_restore'
            } as Waybill;
        }));

        // 2. Write to IDB (Synchronous batch in a single transaction)
        const db = await getDb();
        const tx = db.transaction('waybills', 'readwrite');
        const store = tx.objectStore('waybills');
        await Promise.all(processedItems.map(item => store.put(item)));
        await tx.done;
    },

    // --- 🚀 НОВАЯ ЛОГИКА: Списки через In-Memory Sort ---
    // Для поддержки сортировки по любому полю (например validFrom) с пагинацией,
    // мы загружаем отфильтрованные данные в память, сортируем и режем на страницы.
    // Для объемов до 10-20 тыс. записей это работает достаточно быстро (<50ms).

    async list({
        page = 1,
        pageSize = 20,
        filters = {},
        sortBy = 'date',
        sortDir = 'desc'
    }: {
        page?: number;
        pageSize?: number;
        filters?: { vehicleId?: string; dateFrom?: string; dateTo?: string; status?: string; driverId?: string };
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
    }): Promise<Waybill[]> {
        await ensureMigration();

        const db = await getDb();
        const tx = db.transaction('waybills', 'readonly');

        // 1. Выбираем стратегию выборки кандидатов (используем индексы для фильтрации)
        let source: any = tx.store;
        let range: IDBKeyRange | null = null;
        let indexUsed = false;

        // Стратегия 1: Фильтр по дате (самый селективный)
        if (filters.dateFrom || filters.dateTo) {
            source = tx.store.index('by-date');
            indexUsed = true;
            if (filters.dateFrom && filters.dateTo) {
                range = IDBKeyRange.bound(filters.dateFrom, filters.dateTo);
            } else if (filters.dateFrom) {
                range = IDBKeyRange.lowerBound(filters.dateFrom);
            } else {
                range = IDBKeyRange.upperBound(filters.dateTo);
            }
        }
        // Стратегия 2: Фильтр по машине
        else if (filters.vehicleId) {
            source = tx.store.index('by-vehicle');
            indexUsed = true;
            range = IDBKeyRange.only(filters.vehicleId);
        }
        // Стратегия 3: Фильтр по статусу
        else if (filters.status) {
            source = tx.store.index('by-status');
            indexUsed = true;
            range = IDBKeyRange.only(filters.status);
        }

        // 2. Получаем всех кандидатов
        let allMatches: Waybill[] = [];
        if (indexUsed) {
            allMatches = await source.getAll(range);
        } else {
            allMatches = await tx.store.getAll();
        }

        // 3. Остаточная фильтрация в JS
        const filtered = allMatches.filter(doc => {
            // Если шли по индексу, некоторые проверки избыточны, но безопасны
            if (filters.vehicleId && doc.vehicleId !== filters.vehicleId) return false;
            if (filters.dateFrom && doc.date < filters.dateFrom) return false;
            if (filters.dateTo && doc.date > filters.dateTo) return false;
            if (filters.status && doc.status !== filters.status) return false;
            if (filters.driverId && doc.driverId !== filters.driverId) return false;
            return true;
        });

        // 4. Сортировка в памяти
        filtered.sort((a, b) => {
            const valA = (a as any)[sortBy];
            const valB = (b as any)[sortBy];

            if (valA === valB) return 0;
            if (valA === null || valA === undefined) return 1; // nulls last
            if (valB === null || valB === undefined) return -1;

            if (valA < valB) return sortDir === 'asc' ? -1 : 1;
            if (valA > valB) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        // 5. Пагинация
        const startIndex = (page - 1) * pageSize;
        return filtered.slice(startIndex, startIndex + pageSize);
    },

    async count(filters: { vehicleId?: string; dateFrom?: string; dateTo?: string; status?: string; driverId?: string } = {}): Promise<number> {
        await ensureMigration();
        const db = await getDb();
        const tx = db.transaction('waybills', 'readonly');

        // Упрощенный count: если фильтры сложные, проще получить все и посчитать длину массива.
        // Для IDB getAllKeys быстрее, чем getAll.

        // Повторяем логику выбора индекса
        let source: any = tx.store;
        let range: IDBKeyRange | null = null;
        let indexUsed = false;

        if (filters.dateFrom || filters.dateTo) {
            source = tx.store.index('by-date');
            indexUsed = true;
            if (filters.dateFrom && filters.dateTo) range = IDBKeyRange.bound(filters.dateFrom, filters.dateTo);
            else if (filters.dateFrom) range = IDBKeyRange.lowerBound(filters.dateFrom);
            else range = IDBKeyRange.upperBound(filters.dateTo);
        } else if (filters.vehicleId) {
            source = tx.store.index('by-vehicle');
            indexUsed = true;
            range = IDBKeyRange.only(filters.vehicleId);
        } else if (filters.status) {
            source = tx.store.index('by-status');
            indexUsed = true;
            range = IDBKeyRange.only(filters.status);
        }

        // Если фильтры совпадают с индексом 1-в-1, используем нативный count
        const hasResidualFilters =
            (indexUsed && filters.vehicleId && !source.name.includes('vehicle')) ||
            (indexUsed && (filters.dateFrom || filters.dateTo) && !source.name.includes('date')) ||
            (indexUsed && filters.status && !source.name.includes('status')) ||
            filters.driverId;

        if (!hasResidualFilters && indexUsed) {
            return await source.count(range);
        }

        if (!hasResidualFilters && !indexUsed && Object.keys(filters).length === 0) {
            return await source.count();
        }

        // Иначе читаем все и фильтруем (getAll чуть медленнее, но надежнее)
        // Используем cursor для memory efficiency при подсчете
        let count = 0;
        let cursor = await source.openCursor(range);
        while (cursor) {
            const doc = cursor.value as Waybill;
            let match = true;
            if (filters.vehicleId && doc.vehicleId !== filters.vehicleId) match = false;
            if (filters.dateFrom && doc.date < filters.dateFrom) match = false;
            if (filters.dateTo && doc.date > filters.dateTo) match = false;
            if (filters.status && doc.status !== filters.status) match = false;
            if (filters.driverId && doc.driverId !== filters.driverId) match = false;

            if (match) count++;
            cursor = await cursor.continue();
        }
        return count;
    }
};

/**
 * DATA MINIMIZATION FILTER:
 * Only sync non-sensitive fields to Firestore for mobile app pairing.
 * Sensitive data (routes, notes, attachments) stays in local IndexedDB only.
 */
export async function filterWaybillForCloud(wb: Waybill) {
    const security = await cloudSync.getEncryptionParams();

    // Sensitive field: number 
    const waybillNumber = security
        ? await encrypt(wb.number, security.key || security.password, security.salt)
        : wb.number;

    // Sensitive field: routes (JSON -> Encrypt)
    // We send routes ONLY if encryption is available to protect detailed movements
    let encryptedRoutes: string | any[] = [];
    if (security && wb.routes && wb.routes.length > 0) {
        try {
            const routesJson = JSON.stringify(wb.routes);
            encryptedRoutes = await encrypt(routesJson, security.key || security.password, security.salt);
        } catch (e) {
            console.error('[WaybillRepo] Failed to encrypt routes', e);
            encryptedRoutes = []; // Fail safe: do not send plain routes
        }
    } else if (!security) {
        // If no security (e.g. dev mode without password), we might choose to send plain or nothing.
        // For now, adhering to "Full Backup" request -> send plain if no security set? 
        // Or keep "Safe by default" -> send empty. 
        // Current logic: If user has no password set, they probably haven't set up sync properly anyway.
        // Let's safe-guard: send plain ONLY if we are sure (but here we assume security is configured).
        // To be safe: If no security, send empty to avoid accidental leaks. User MUST set password for full backup.
        encryptedRoutes = [];
    }

    // Sensitive field: notes
    let encryptedNotes = null;
    if (security && wb.notes) {
        encryptedNotes = await encrypt(wb.notes, security.key || security.password, security.salt);
    }

    return {
        id: wb.id,
        number: waybillNumber,
        date: wb.date,
        vehicleId: wb.vehicleId,
        driverId: wb.driverId,
        assignedDriverUid: wb.assignedDriverUid ?? null,
        status: wb.status,
        odometerStart: wb.odometerStart,
        odometerEnd: wb.odometerEnd ?? null,
        fuelAtStart: wb.fuelAtStart,
        fuelFilled: wb.fuelFilled ?? 0,
        fuelAtEnd: wb.fuelAtEnd ?? null,
        fuelPlanned: wb.fuelPlanned ?? null,
        organizationId: wb.organizationId,
        dispatcherId: wb.dispatcherId ?? null,
        controllerId: wb.controllerId ?? null,
        reviewerId: wb.reviewerId ?? null,
        validFrom: wb.validFrom,
        validTo: wb.validTo,
        calculationMethod: wb.calculationMethod ?? 'by_total',
        customFuelRate: wb.customFuelRate ?? null,
        blankId: wb.blankId ?? null,
        blankSeries: wb.blankSeries ?? null,
        blankNumber: wb.blankNumber ?? null,
        postedAt: wb.postedAt ?? null,
        postedBy: wb.postedBy ?? null,
        // --- Full Backup Fields ---
        routes: encryptedRoutes,
        notes: encryptedNotes,
        // --------------------------
        updatedAt: new Date().toISOString()
    };
}
